import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/core/shared/v2/ui/button";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";

type TermsAgreementProps = {
  content: ReactNode;
  onAccept: () => void;
};

const SCROLL_BOTTOM_THRESHOLD_PX = 16;

export const TermsAgreement = ({ content, onAccept }: TermsAgreementProps) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [reachedBottom, setReachedBottom] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const check = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX) {
        setReachedBottom(true);
      }
    };

    check();
    el.addEventListener("scroll", check, { passive: true });
    return () => el.removeEventListener("scroll", check);
  }, []);

  return (
    <IntersectionFadeIn className="flex h-auto w-full flex-1 items-start justify-center max-lg:p-4">
      <div className="mx-auto flex w-full max-w-3xl flex-col rounded-2xl bg-card px-4 py-6 md:p-8 lg:p-12">
        <h3 className="font-serif text-2xl text-card-foreground lg:text-3xl">
          {t("VOUCHERS_DISTRIBUTION.TERMS_AGREEMENT.TITLE")}
        </h3>
        <p className="mt-2 text-base text-muted-foreground">{t("VOUCHERS_DISTRIBUTION.TERMS_AGREEMENT.DESCRIPTION")}</p>
        <div
          ref={scrollRef}
          className="mt-6 max-h-[55vh] overflow-y-auto rounded-xl border border-border bg-background p-4 text-card-foreground text-sm leading-relaxed"
        >
          {content}
        </div>
        <Button className="mt-6 self-center" size="lg" disabled={!reachedBottom} onClick={onAccept}>
          {t("VOUCHERS_DISTRIBUTION.TERMS_AGREEMENT.CTA")}
        </Button>
      </div>
    </IntersectionFadeIn>
  );
};
